
import type {
  Identifiable,
  ProductDomainService,
  QueryRanking,
  QueryRankingHiddenElementDTO,
} from '@empathyco/playboard-core-domain'
import type { PropType, SetupContext } from 'vue'
import type { QueryRankingElementActionConfig } from '../../composables/use-query-ranking-element-actions-config'
import type { ToolsMessages } from '../../models/i18n/tools.messages'
import type { QueryRankingDomainService } from '../../services/interfaces/domain/query-ranking-domain.service'
import type { EpbKeywordsItemId } from '../shared/epb-keywords'
import type { BaseQueryRankingModalAcceptEventPayload } from './base-query-ranking-modal.types'
import type { AcceptEventPayload } from './use-query-ranking-modal'
import { HiddenElementsAction } from '@empathyco/playboard-core-domain'
import { defineComponent, ref, watch } from 'vue'
import { CONTAINER } from '../../config/inversify.config'
import { TYPES } from '../../config/inversify.types'
import { ConfigurationsUtils } from '../../utils/configurations.utils'
import { EpbKeywords } from '../shared/epb-keywords'
import BaseQueryRankingModal from './base-query-ranking-modal.component.vue'
import { useQueryRankingModal } from './use-query-ranking-modal'

type HiddenProductsQueryRankingModalAcceptEventPayload = Array<QueryRankingHiddenElementDTO>

export default defineComponent({
  name: 'HiddenProductsQueryRankingModal',
  components: {
    EpbKeywords,
    BaseQueryRankingModal,
  },
  props: {
    messages: {
      type: Object as PropType<ToolsMessages['configurations']>,
      required: true,
    },
    queryRanking: {
      type: Object as PropType<QueryRanking>,
      required: true,
    },
    queryRankingConfig: {
      type: Object as PropType<QueryRankingHiddenElementDTO & Identifiable>,
      required: false,
      default: null,
    },
  },
  emits: {
    close: () => {},
    accept: (payload: HiddenProductsQueryRankingModalAcceptEventPayload) => {},
  },
  setup(props, ctx: SetupContext) {
    const initialFieldValues = {
      productIds: [],
    }

    const actions: Array<QueryRankingElementActionConfig['action']> = [HiddenElementsAction]

    const productDomainService = CONTAINER.get<ProductDomainService>(TYPES.ProductDomainService)
    const qrDomainService = CONTAINER.get<QueryRankingDomainService>(
      TYPES.QueryRankingDomainService,
    )

    const modal = ref()

    const queryRankingModal = useQueryRankingModal<
      HiddenProductsQueryRankingModalAcceptEventPayload,
      BaseQueryRankingModalAcceptEventPayload
    >({ resetFields, buildAcceptEventPayload, modal }, ctx)

    const productIds = ref([...initialFieldValues.productIds])
    const productIdsRef = ref()
    const isFormValid = ref(validateForm())

    const validateIds = (productIds: string[]): Promise<EpbKeywordsItemId[]> => {
      const validatedProducts = productIds.map(id => {
        const isIdValid = productDomainService.isProductIdValid(id)
        if (!isIdValid) {
          return {
            id,
            valid: false,
            tooltip: props.messages.fields.hiddenProducts.invalidProductID,
          }
        }
        const hasConflict = qrDomainService.areHiddenProductFieldsInConflict(props.queryRanking, {
          id: props.queryRankingConfig
            ? ConfigurationsUtils.getRankingElementId(props.queryRankingConfig)
            : undefined,
          fieldName: qrDomainService.getProductIdField() ?? '',
          fieldValue: id,
          schedule: queryRankingModal.schedule.value,
        })

        return {
          id,
          valid: !hasConflict,
          tooltip: hasConflict ? props.messages.modals.hiddenProducts.overlappedResult : undefined,
        }
      })
      return Promise.resolve(validatedProducts)
    }

    function validateForm(): boolean {
      return (
        !!props.queryRankingConfig &&
        !qrDomainService.areHiddenProductFieldsInConflict(props.queryRanking, {
          ...props.queryRankingConfig,
          schedule: queryRankingModal.schedule.value,
        })
      )
    }

    function resetFields(): void {
      isFormValid.value = false
      productIds.value = props.queryRankingConfig
        ? [props.queryRankingConfig.fieldValue]
        : [...initialFieldValues.productIds]
    }

    function buildAcceptEventPayload(
      acceptEventPayload: AcceptEventPayload,
    ): HiddenProductsQueryRankingModalAcceptEventPayload {
      return productIds.value.map((productId: string) => ({
        fieldName: qrDomainService.getProductIdField() ?? '',
        fieldValue: productId,
        schedule: acceptEventPayload.schedule,
        action: HiddenElementsAction,
        filters: {},
      }))
    }

    watch(
      () => queryRankingModal.schedule,
      () => {
        if (props.queryRankingConfig) {
          isFormValid.value = validateForm()
        }

        if (!props.queryRankingConfig && productIds.value.length) {
          productIdsRef.value?.validatePillIds()
        }
      },
      { deep: true },
    )

    return {
      ...queryRankingModal,
      actions,
      productIdsRef,
      isFormValid,
      productIds,
      validateIds,
      modal,
    }
  },
})
